// Active medication orders for one admission, with the doctor's Cancel action
// (CPOE T11). Orders are APPEND-ONLY (D7): no in-place edits of a sent order —
// discontinue = Cancel here (+ reorder via the care-plan modal for a dose
// change). Cancelling calls update_ipd_prescription_status/ which also retires
// the order's remaining dose tasks to 'cancelled' on the backend; cancelling
// after dispensing doesn't undo dispensing — the leftover stock shows up in
// the returnable computation instead.
//
// Reads the order book (main_ipd_prescription — one row per medicine, rows of
// an order share order_no) via get_ipd_prescriptions_by_admission/.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Typography, Chip, Stack,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";

const { REACT_APP_BACKEND_ROUTE_URL } = process.env;

const getApiHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
  "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
  "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
  USERNAME: sessionStorage.getItem("userName") || "",
});

const STATUS_COLOR = {
  Sent: "default",
  Acknowledged: "info",
  Dispensed: "warning",
  Received: "success",
  Cancel: "error",
};

// Same vocabulary as the order-management drawer's cancel flow.
const CANCEL_REASONS = [
  "Wrong Patient", "Duplicate Order", "Entered by Mistake",
  "Clinical Decision Changed", "Other",
];

export default function MedicationOrdersPanel({ admissionId, disabled = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null); // order_no
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(() => {
    if (!admissionId) return;
    setLoading(true);
    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_ipd_prescriptions_by_admission/`,
        { admission_id: admissionId },
        { headers: getApiHeaders() }
      )
      .then((r) => {
        const list = Array.isArray(r.data?.data) ? r.data.data : [];
        setRows(list);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [admissionId]);

  useEffect(reload, [reload]);

  const byOrder = useMemo(() => {
    const groups = {};
    rows.forEach((r) => {
      const key = r.order_no || `#${r.id}`;
      (groups[key] = groups[key] || []).push(r);
    });
    return groups;
  }, [rows]);

  const cancellable = (meds) => meds.some((m) => m.status !== "Cancel");

  const confirmCancel = async () => {
    if (!cancelTarget || !reason) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}update_ipd_prescription_status/`,
        {
          admission_id: admissionId,
          status: "Cancel",
          order_no: cancelTarget,
          reason: reason === "Other" && reasonNote ? reasonNote : reason,
        },
        { headers: getApiHeaders() }
      );
      const body = res.data || {};
      if (body.status === "Success" && (body.updated_ids || []).length > 0) {
        toast.success(
          `Order ${cancelTarget} cancelled${body.tasks_cancelled ? ` — ${body.tasks_cancelled} pending dose task(s) removed from the nurse queue` : ""}.`
        );
        setCancelTarget(null);
        setReason("");
        setReasonNote("");
        reload();
      } else {
        toast.error(body.rejected?.[0]?.reason || body.message || "Could not cancel this order.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not cancel this order.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ pb: 1, opacity: disabled ? 0.5 : 1 }}>
      <Accordion
        defaultExpanded={false}
        disabled={disabled}
        elevation={0}
        disableGutters
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Medication Orders
            </Typography>
            {rows.length > 0 && (
              <Chip size="small" label={Object.keys(byOrder).length} sx={{ ml: "auto" }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No medication orders for this admission.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {Object.entries(byOrder).map(([orderNo, meds]) => (
                <Box
                  key={orderNo}
                  sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{orderNo}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {meds[0]?.doctor_name ? `Dr. ${meds[0].doctor_name}` : ""}
                    </Typography>
                    {cancellable(meds) && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ ml: "auto", textTransform: "none", py: 0, minWidth: 0 }}
                        disabled={disabled}
                        onClick={() => setCancelTarget(orderNo)}
                      >
                        Cancel order
                      </Button>
                    )}
                  </Box>
                  <Stack spacing={0.5}>
                    {meds.map((m) => (
                      <Box key={m.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                          {m.medicine_name || "—"}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={m.status || "Sent"}
                          color={STATUS_COLOR[m.status] || "default"}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog open={!!cancelTarget} onClose={submitting ? undefined : () => setCancelTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Cancel order {cancelTarget}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Remaining scheduled doses will be removed from the nurse work queue
            (completed doses are untouched). Already-dispensed stock is not
            undone — it becomes returnable. For a dose change, cancel and place
            a new order.
          </Typography>
          <TextField
            select fullWidth size="small" required label="Reason"
            value={reason} onChange={(e) => setReason(e.target.value)}
          >
            {CANCEL_REASONS.map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
          </TextField>
          {reason === "Other" && (
            <TextField
              fullWidth size="small" sx={{ mt: 1.5 }} label="Details"
              value={reasonNote} onChange={(e) => setReasonNote(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={submitting}>Back</Button>
          <Button color="error" variant="contained" onClick={confirmCancel} disabled={submitting || !reason}>
            {submitting ? "Cancelling…" : "Cancel Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
